import {CourseContentUseCase} from "./content_interface";
import {
  Content,
  ContentAnnotation,
  ContentComplement,
  ContentDoubt, Question, QuestionStatus, ScormCheckpoint
} from "../../../../infrastructure/presentation/views/content/helpers";
import {Rating, RatingList} from "../../../entities/rating";
import courseContentRepository from "../../../../infrastructure/repository/content_provider/course/content_repository";
import {
  processAnnotation,
  processCheckpointTimestamp,
  processDoubt,
  processProgressSpeedRate,
  processRating
} from "../rules";
import {Instructor} from "../../../entities/entities";


class CourseContentUseCaseImpl implements CourseContentUseCase {
  async getAllContents(courseID: number): Promise<Content[]> {
    return await courseContentRepository.getAllContents(courseID);
  }

  async getContentByID(courseID: number, contentID: number): Promise<Content> {
    return await courseContentRepository.getContentByID(courseID, contentID);
  }

  async initializeContent(courseID: number, contentID: number): Promise<void> {
    return await courseContentRepository.initializeContent(courseID, contentID);
  }

  async getAllComplements(courseID: number, contentID: number): Promise<ContentComplement[]> {
    return await courseContentRepository.getAllComplements(courseID, contentID);
  }

  async createRating(courseID: number, contentID: number, rating: Rating): Promise<Rating> {
    processRating(rating);
    return await courseContentRepository.createRating(courseID, contentID, rating);
  }

  async getAllRatings(courseID: number, contentID: number,  index?: number): Promise<RatingList> {
    return await courseContentRepository.getAllRatings(courseID, contentID, index);
  }
  async likeRating(ratingID: number): Promise<void> {
    return await courseContentRepository.likeRating(ratingID);
  }

  async removeLikeRating(ratingID: number): Promise<void> {
    return await courseContentRepository.removeLikeRating(ratingID);
  }

  async deleteRating(courseID: number, contentID: number, ratingID: number): Promise<void> {
    return await courseContentRepository.deleteRating(courseID, contentID, ratingID);
  }

  async createDoubt(courseID: number, contentID: number, contentDoubt: ContentDoubt): Promise<ContentDoubt> {
    processDoubt(contentDoubt);
    return await courseContentRepository.createDoubt(courseID, contentID, contentDoubt);
  }

  async getAllDoubts(courseID: number, contentID: number): Promise<ContentDoubt[]> {
    return await courseContentRepository.getAllDoubts(courseID, contentID);
  }

  async getContentInstructors(courseID: number, contentID: number): Promise<Instructor[]> {
    return await courseContentRepository.getContentInstructors(courseID, contentID);
  }

  async getAllContentAnnotations(courseID: number, contentID: number): Promise<ContentAnnotation[]> {
    return await courseContentRepository.getAllContentAnnotations(courseID, contentID);
  }

  async updateContentAnnotation(courseID: number, contentID: number, annotation: ContentAnnotation): Promise<ContentAnnotation> {
    return await courseContentRepository.updateContentAnnotation(courseID, contentID, annotation);
  }

  async deleteContentAnnotation(courseID: number, contentID: number, annotationID: number): Promise<void> {
    return await courseContentRepository.deleteContentAnnotation(courseID, contentID, annotationID);
  }

  async createContentAnnotation(courseID: number, contentID: number, annotation: ContentAnnotation): Promise<ContentAnnotation> {
    processAnnotation(annotation);
    return await courseContentRepository.createContentAnnotation(courseID, contentID, annotation);
  }

  async saveCheckpointSeconds(courseID: number, contentID: number, timestamp: number): Promise<void> {
    processCheckpointTimestamp(timestamp);
    return await courseContentRepository.saveCheckpointSeconds(courseID, contentID, timestamp);
  }

  async saveCheckpointSCORM(courseID: number, contentID: number, scormCheckpoint: ScormCheckpoint): Promise<void> {
    return await courseContentRepository.saveCheckpointSCORM(courseID, contentID, scormCheckpoint);
  }

  async saveProgress(courseID: number, contentID: number, speedRate: number): Promise<number> {
    processProgressSpeedRate(speedRate);
    return await courseContentRepository.saveProgress(courseID, contentID, speedRate);
  }

  async generateQuestionnaire(courseID: number, contentID: number): Promise<number> {
    return await courseContentRepository.generateQuestionnaire(courseID, contentID);
  }

  async getQuestionByPage(courseID: number, contentID: number, questionnaireID: number, page: number): Promise<Question> {
    return await courseContentRepository.getQuestionByPage(courseID, contentID, questionnaireID, page);
  };

  async replyQuestionByPage(courseID: number, contentID: number, questionnaireID: number, page: number, alternativeID: number): Promise<QuestionStatus> {
    return await courseContentRepository.replyQuestionByPage(courseID, contentID, questionnaireID, page, alternativeID);
  };

  async replyDescriptiveQuestionByPage(courseID: number, contentID: number, questionnaireID: number, page: number, answer: string): Promise<QuestionStatus> {
    return await courseContentRepository.replyDescriptiveQuestionByPage(courseID, contentID, questionnaireID, page, answer);
  }

  async finishContent(courseID: number, contentID: number): Promise<void> {
    return await courseContentRepository.finishContent(courseID, contentID);
  }
}

export default new CourseContentUseCaseImpl() as CourseContentUseCase;
